import { HStack, PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { DataTable, RowStatus } from '@/components/DataTable'
import { TableHeader } from '@/components/DataTable/CellContent/TableHeader'
import { DateFilter } from '@/components/DataTable/DateFilter'
import { useDateRange } from '@/components/DatePicker/hooks'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { NewDropdown } from '@/components/NewDropdown'
import { useQueryDefaults } from '@/hooks/queries/defaults/useQueryDefaults'
import { useAuditTrailById, useAuditTrails } from '@/hooks/queries/credit/useAuditTrails'
import { AuditTrails, auditTrailsSearchSchema } from '@/hooks/queries/useAuditTrails'
import { useTablePagination } from '@/hooks/useTablePagination'
import { useSelectedFilters } from '@/lib/utils/filters'
import { DATATABLE_NAMES } from '@/stores/datatable'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { getModuleEventTypes } from '@repo/permissions'
import { Button, Separator } from '@repo/ui/components'
import { getFormattedDate, getUserFullname } from '@repo/utils'
import { createFileRoute } from '@tanstack/react-router'
import { ColumnDef, ColumnSort } from '@tanstack/react-table'
import { format, parse } from 'date-fns'
import { ChevronRight, RefreshCcw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import AuditDetailsSheet from './-components/AuditDetailsSheet'

export const Route = createFileRoute('/_auth/_modules/credit/admin/audit-trails/')({
  component: AuditTrailsPage,
  validateSearch: (search) => auditTrailsSearchSchema.parse(search),
})

export type AuditTrailsTableRow = AuditTrails['auditTrails'][number] & { rowStatus?: RowStatus }

function AuditTrailsPage() {
  const { t } = useTranslation('admin')
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const availableEventTypes = getModuleEventTypes('credit') ?? []

  const { pageSize, onPageSizeChange } = useTablePagination({
    navigate,
    search,
  })

  useQueryDefaults({
    search,
    navigate,
    defaults: {
      createdAt: format(new Date(), 'yyyy-MM-dd'),
    },
  })

  const { isDateInRange } = useDateRange({
    numberOfDaysBefore: 365,
    numberOfDaysAfter: 0,
  })

  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')
  const [eventType, setEventType] = useState<string[]>(search.eventType ?? [])
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false)
  const [selectedAuditTrailId, setSelectedAuditTrailId] = useState<number | undefined>(undefined)

  const selectedFilters = useSelectedFilters({ eventType, keyword })

  const areAnyFiltersApplied =
    Object.values(selectedFilters).some((value) => value !== undefined) ||
    search.eventType !== undefined

  const {
    isPending: isAuditTrailsPending,
    data: { auditTrails, count } = { auditTrails: [], count: 0 },
    error: auditTrailsError,
  } = useAuditTrails({ ...search, page: search.page ?? 1, pageSize })

  const {
    isPending: isAuditTrailPending,
    data: auditTrail,
    error: auditTrailError,
  } = useAuditTrailById(selectedAuditTrailId)

  const onPageChange = (page: number) => {
    navigate({ search: { ...search, page: page + 1 } })
  }

  const resetAllFilters = () => {
    setKeyword('')
    setEventType([])

    navigate({
      search: { ...search, eventType: undefined, keyword: undefined },
    })
  }

  const onOpenDetailSheet = (id: number) => {
    setSelectedAuditTrailId(id)
    setIsDetailSheetOpen(true)
  }

  const onSelectDate = (date: Date | undefined) => {
    navigate({
      search: {
        ...search,
        createdAt: date ? format(date, 'yyyy-MM-dd') : undefined,
      },
    })
  }

  const onSortChange = (sort: ColumnSort) => {
    navigate({
      search: {
        ...search,
        sort,
      },
    })
  }

  const onShowResults = () => {
    navigate({
      search: { ...search, ...selectedFilters, keyword: keyword || undefined, page: 1 },
    })
  }

  const columns: ColumnDef<AuditTrailsTableRow>[] = [
    {
      id: 'fullName',
      accessorKey: 'fullName',
      header: ({ column }) => <TableHeader column={column} />,
      cell: ({ row }) => (
        <VStack gap="xs">
          <Text>{row.original.user && getUserFullname(row.original.user)}</Text>
          <Text tone="muted" as="span" size="xs">
            {row.original.createdBy.split('@')[0]}
          </Text>
        </VStack>
      ),
      meta: {
        displayTitle: t('auditTrailsDataTable.user'),
        cellClassName: 'truncate',
      },
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => getFormattedDate(row.original.createdAt),
      meta: {
        displayTitle: t('auditTrailsDataTable.createdAt'),
        cellClassName: 'truncate',
      },
    },
    {
      id: 'eventType',
      accessorKey: 'eventType',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => t(`eventTypes.${row.original.eventType}`) || '-',
      meta: {
        displayTitle: t('auditTrailsDataTable.eventType'),
        cellClassName: 'truncate',
      },
    },
    {
      id: 'eventDetails',
      cell: ({ row }) => (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onOpenDetailSheet(row.original.id)}
        >
          <ChevronRight className="size-4" />
        </Button>
      ),
      meta: {
        hideInColumnsMenu: true,
        cellClassName: 'w-0',
      },
    },
  ]

  return (
    <PageContainer>
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.AUDIT_TRAILS}
        title={t('tutorials.dialog.auditTrails.title')}
        guideUrl="/admin/audit-trails"
      >
        {t('tutorials.dialog.auditTrails.description')}
      </IntroductionDialog>

      <Spread>
        <PageHeader>{t('auditTrailsPage.header')}</PageHeader>
        <DateFilter
          disabled={(date) => !isDateInRange(date)}
          defaultDate={
            search.createdAt ? parse(search.createdAt, 'yyyy-MM-dd', new Date()) : undefined
          }
          onSelect={onSelectDate}
        />
      </Spread>

      <Card>
        <CardContent className="py-6">
          <HStack gap="sm" className="w-full min-w-0 flex-nowrap justify-between">
            <div className="w-full max-w-[400px]">
              <NewDropdown
                placeholder={t('auditTrailsPage.eventTypeDropdown.placeholder')}
                options={availableEventTypes.map((eventType) => ({
                  value: eventType,
                  label: t(`eventTypes.${eventType}`),
                }))}
                value={eventType}
                onChange={setEventType}
                selectionMode="multiple"
                searchable
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-none"
                disabled={!areAnyFiltersApplied}
                onClick={resetAllFilters}
              >
                {t('auditTrailsPage.clearButton')}
              </Button>
              <Button
                type="button"
                onClick={onShowResults}
                className="flex-none"
                disabled={isAuditTrailsPending || !areAnyFiltersApplied}
              >
                <RefreshCcw className="size-4" />
                {t('auditTrailsPage.applyFiltersButton')}
              </Button>
            </div>
          </HStack>

          <Separator />

          <DataTable
            name={DATATABLE_NAMES.AUDIT_TRAILS}
            columns={columns}
            data={auditTrails as unknown as AuditTrailsTableRow[]}
            isPending={isAuditTrailsPending}
            error={auditTrailsError}
            rowCount={count}
            page={search.page}
            search={{
              value: keyword,
              setValue: setKeyword,
              placeholder: t('auditTrailsPage.searchPlaceholder'),
            }}
            sort={search.sort}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onShowResults={onShowResults}
            onSortChange={onSortChange}
            isSortedClientSide={false}
          />
        </CardContent>
      </Card>

      <AuditDetailsSheet
        open={isDetailSheetOpen}
        onOpenChange={setIsDetailSheetOpen}
        isPending={isAuditTrailPending}
        auditTrail={auditTrail}
        error={auditTrailError}
      />
    </PageContainer>
  )
}

export default AuditTrailsPage
